<template>
    <div v-if="sliderMin < sliderMax" class="drs" ref="wrap">
        <div class="drs-track">
            <div class="drs-fill" :style="fillStyle"></div>
        </div>
        <div
            class="drs-thumb"
            :style="{ left: `calc(8px + ${visualFromFrac} * (100% - 16px))` }"
            @mousedown.prevent="startDrag('from', $event)"
            @touchstart.prevent="startDrag('from', $event)"
        ></div>
        <div
            class="drs-thumb"
            :style="{ left: `calc(8px + ${visualToFrac} * (100% - 16px))` }"
            @mousedown.prevent="startDrag('to', $event)"
            @touchstart.prevent="startDrag('to', $event)"
        ></div>
    </div>
</template>

<script>
const THUMB = 16;
const HALF  = THUMB / 2;

export default {
    name: 'DateRangeSlider',

    props: {
        min:  { type: String, default: null },
        max:  { type: String, default: null },
        from: { type: String, default: null },
        to:   { type: String, default: null },
    },

    data() {
        return {
            localFrom:      0,
            localTo:        0,
            dragging:       null,
            visualFromFrac: 0,
            visualToFrac:   1,
        };
    },

    computed: {
        sliderMin() { return this.dateToDay(this.min); },
        sliderMax() { return this.dateToDay(this.max); },

        fillStyle() {
            return {
                left:  (this.visualFromFrac * 100) + '%',
                right: ((1 - this.visualToFrac) * 100) + '%',
            };
        },
    },

    watch: {
        from: { immediate: true, handler(v) {
            this.localFrom = this.dateToDay(v || this.min);
            if (!this.dragging) this.visualFromFrac = this.dayToFrac(this.localFrom);
        }},
        to: { immediate: true, handler(v) {
            this.localTo = this.dateToDay(v || this.max);
            if (!this.dragging) this.visualToFrac = this.dayToFrac(this.localTo);
        }},
        sliderMin() { if (!this.dragging) this.visualFromFrac = this.dayToFrac(this.localFrom); },
        sliderMax() { if (!this.dragging) this.visualToFrac   = this.dayToFrac(this.localTo); },
    },

    beforeDestroy() {
        this.detachListeners();
    },

    methods: {
        dateToDay(s) {
            if (!s) return 0;
            const [y, m, d] = s.split('-').map(Number);
            return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
        },
        dayToDate(n) {
            const d = new Date(n * 86400000);
            return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
        },
        dayToFrac(day) {
            const range = this.sliderMax - this.sliderMin;
            return range ? (day - this.sliderMin) / range : 0;
        },

        startDrag(which, e) {
            this.dragging = which;
            document.addEventListener('mousemove', this.onMove);
            document.addEventListener('mouseup',   this.onUp);
            document.addEventListener('touchmove', this.onMove, { passive: false });
            document.addEventListener('touchend',  this.onUp);
        },

        onMove(e) {
            e.preventDefault();
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const rect    = this.$refs.wrap.getBoundingClientRect();
            const innerW  = rect.width - THUMB;
            const range   = this.sliderMax - this.sliderMin;

            // Cursor in px, clamped to [HALF, width-HALF] (thumb-border coordinate space)
            const px   = Math.max(HALF, Math.min(rect.width - HALF, clientX - rect.left));
            const frac = (px - HALF) / innerW;
            const day  = Math.round(this.sliderMin + frac * range);

            // Current thumb centers in the same px space
            const fromPx = HALF + this.visualFromFrac * innerW;
            const toPx   = HALF + this.visualToFrac   * innerW;

            if (this.dragging === 'from') {
                const stopPx = toPx - THUMB;
                if (px >= stopPx) {
                    this.visualFromFrac = Math.max(0, (stopPx - HALF) / innerW);
                    this.localFrom      = Math.max(this.sliderMin, this.localTo - 1);
                } else {
                    this.visualFromFrac = frac;
                    this.localFrom      = day;
                }
                this.$emit('update:from', this.dayToDate(this.localFrom));
            } else {
                const stopPx = fromPx + THUMB;
                if (px <= stopPx) {
                    this.visualToFrac = Math.min(1, (stopPx - HALF) / innerW);
                    this.localTo      = Math.min(this.sliderMax, this.localFrom + 1);
                } else {
                    this.visualToFrac = frac;
                    this.localTo      = day;
                }
                this.$emit('update:to', this.dayToDate(this.localTo));
            }
        },

        onUp() {
            this.dragging = null;
            this.detachListeners();
        },

        detachListeners() {
            document.removeEventListener('mousemove', this.onMove);
            document.removeEventListener('mouseup',   this.onUp);
            document.removeEventListener('touchmove', this.onMove);
            document.removeEventListener('touchend',  this.onUp);
        },
    },
};
</script>

<style scoped>
.drs {
    position: relative;
    height: 20px;
    margin: 0.5rem 0 1rem;
}

.drs-track {
    position: absolute;
    left: 8px;
    right: 8px;
    top: 50%;
    height: 4px;
    transform: translateY(-50%);
    background: #dee2e6;
    border-radius: 2px;
}

.drs-fill {
    position: absolute;
    top: 0;
    bottom: 0;
    background: #E20613;
    border-radius: 2px;
}

.drs-thumb {
    position: absolute;
    top: 50%;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #E20613;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px #E20613;
    transform: translate(-50%, -50%);
    cursor: pointer;
    user-select: none;
    touch-action: none;
}
</style>